export interface ChangelogEntry {
  version: string;   // e.g. "0.11.1"
  date: string;      // ISO date string e.g. "2026-05-03"
  label?: string;    // Optional highlight badge e.g. "Beta Release"
  headline: string;  // Short sentence summary
  features: string[];
}

// Newest entry FIRST — badge logic depends on CHANGELOG[0] being the latest.
// To add a future release: prepend a new object to this array.
export const CHANGELOG: ChangelogEntry[] = [
  {
    version: '0.11.19',
    date: '2026-06-10',
    headline: 'Attach photos during interview sessions',
    features: [
      'Attach up to 4 photos to any message mid-conversation — the AI sees them and asks about the people, places, and moments captured',
      'Voice sessions: a new attach button next to the mic queues photos to ride along with your next spoken reply, on web and mobile',
      'Photo chips show upload progress, can be removed before sending, and failed uploads offer a retry',
    ],
  },
  {
    version: '0.11.18',
    date: '2026-06-10',
    headline: 'Smoother interviews and clearer book generation',
    features: [
      'Regenerate a single chapter — with optional direction like "make it warmer" — without rewriting the whole book',
      'Interview sessions: if a turn fails mid-conversation, a Retry button re-sends your last answer instead of losing it',
      'Book generation now shows "Chapter X of Y" progress and recovers with a Retry option if the connection stalls',
      'After regenerating, a summary shows how many chapters were rewritten and how many locked chapters were preserved',
      'Locked chapters now show a lock badge in the chapter list before you regenerate',
      'AI passage editing shows a "Rewriting…" indicator while working and a Retry button if it fails',
    ],
  },
  {
    version: '0.11.17',
    date: '2026-05-27',
    headline: 'Chapters now appear in your book; PDF generation fixed',
    features: [
      'Fixed: chapters now correctly save and appear in My Book after generating',
      'Fixed: PDF generation now works — book.html is rebuilt from your latest content after each generate',
      'Mobile: Title Page and Dedication are now edited from their section rows, not from Book Settings',
      'Mobile: Tapping the Title Page row opens a dedicated editor for book title and author line',
    ],
  },
  {
    version: '0.11.16',
    date: '2026-05-22',
    headline: 'Download & Share Your PDF Memoir',
    features: [
      'Download PDF — opens your server-generated memoir PDF directly in the browser',
      'Share PDF — copies a presigned link to your clipboard for easy sharing',
      'Removed the old browser-print PDF path; server-generated PDF is cleaner and more reliable',
    ],
  },
  {
    version: '0.11.15',
    date: '2026-05-22',
    headline: 'Print jobs now submitted to Lulu after payment, confirmation email fixed.',
    features: [
      'Print jobs now submitted to Lulu after payment completes',
      'Order confirmation email now sent correctly after payment',
    ],
  },
  {
    version: '0.11.14',
    date: '2026-05-13',
    headline: 'Deleting your account now removes all your data, including your beta signup.',
    features: [
      'Full account deletion — when you delete your account, your beta signup record is also removed so no trace of your data remains',
    ],
  },
  {
    version: '0.11.13',
    date: '2026-05-08',
    headline: 'Sign in with Google or Apple — on web and mobile.',
    features: [
      'Google Sign-In — use your Google account to sign in or create an account on web and mobile',
      'Apple Sign-In — sign in with Apple ID on web and mobile',
      'MyLife brand kit — mobile app fully updated with dark navy and bronze color palette',
      'Stale session fix — expired tokens are now silently refreshed so you stay signed in without interruption',
    ],
  },
  {
    version: '0.11.12',
    date: '2026-05-07',
    headline: 'Skip out of the life areas wizard any time.',
    features: [
      'Exit wizard — a subtle "Skip for now" link on the intro screen and "Exit wizard" link on each step let you leave the planner without completing it',
    ],
  },
  {
    version: '0.11.9',
    date: '2026-05-07',
    headline: 'Upload your resume and skip onboarding when you\'re ready.',
    features: [
      'Resume upload — add your resume during career setup and Claude uses it to ask smarter, personalized career questions',
      'Skip to Dashboard — jump straight into the app at any point during onboarding without completing every wizard step',
    ],
  },
  {
    version: '0.11.8',
    date: '2026-05-07',
    headline: 'Photo sessions are smarter; audio starts more reliably.',
    features: [
      'Photo sessions now visually examine your photo — Claude describes what it sees and asks specific questions about the people, place, or moment captured',
      'Reliable audio start — audio is now primed during the mode-selection tap so recordings begin immediately without delay',
    ],
  },
  {
    version: '0.11.7',
    date: '2026-05-06',
    headline: 'Dashboard navigation buttons no longer overflow on wider screens.',
    features: [
      'Fixed dashboard menu — all five navigation buttons now wrap cleanly instead of extending past the card border',
    ],
  },
  {
    version: '0.11.6',
    date: '2026-05-06',
    headline: 'Smarter sessions — Claude stays focused on your chosen story.',
    features: [
      'Story-focused sessions — accept a story suggestion and Claude opens on that exact story and stays with it until it\'s fully captured',
      'Smarter story suggestions — only untold stories are offered, picked randomly so every story gets a fair chance',
      'Tangent trap — Claude acknowledges passing mentions in one sentence, then returns to your current story instead of chasing new threads',
      'Graceful session endings — once your story\'s key details are captured, Claude delivers a warm closing summary and invites you to wrap up',
    ],
  },
  {
    version: '0.11.1',
    date: '2026-05-03',
    label: 'Beta Release',
    headline: 'Everything you need to capture and publish your memoir.',
    features: [
      'AI-guided interviews — speak or type; Claude asks the questions, you tell the stories',
      'Photo memories — upload a photo and let it spark a session',
      'Story plan — map out the stories you want to tell across 15 life areas',
      'Life progress tracking — see which areas are in progress and which are complete',
      'WYSIWYG book studio — edit your memoir chapters like a word processor',
      'AI editing assistant — select any passage and ask Claude to improve it',
      'Chapter lock — protect finished chapters from accidental edits',
      'Version snapshots — save and restore named versions before major rewrites',
      'PDF export — one-click print-ready PDF for Blurb, Shutterfly, or home printing',
      'Session history — browse and continue any past interview session',
    ],
  },
];

// localStorage key for tracking which version the user has seen.
// UX-only flag — not security-critical. See IosVoiceWarning.tsx for the same pattern.
const SEEN_VERSION_KEY = 'whats_new_seen_version';

/**
 * Returns the latest version string if there is an unseen update, null if all seen.
 * Read synchronously in the Dashboard render body — no useState/useEffect needed.
 */
export function getUnseenVersion(): string | null {
  const latest = CHANGELOG[0]?.version ?? null;
  if (!latest) return null;
  const seen = localStorage.getItem(SEEN_VERSION_KEY);
  return seen === latest ? null : latest;
}

/** Call on /whats-new mount to clear the Dashboard badge. */
export function markChangelogSeen(): void {
  if (CHANGELOG[0]) {
    localStorage.setItem(SEEN_VERSION_KEY, CHANGELOG[0].version);
  }
}
